import { Tag } from "@prisma/client";
import { message } from "antd";
import { useCallback, useContext, useState } from "react";
import canUser from "../../shared/can-user";
import Creatable from "../../shared/creatable";
import Permissions from "../../shared/permissions";
import { AppContext } from "../contexts/app-context";
import HttpClient from "../http-client";
const size = 20;

export type TagWithMeta = Tag & {
  deleted?: boolean;
  added?: boolean;
};

/* eslint-disable-next-line */
export interface TagListProps {
  value?: TagWithMeta[];
  onChange?: (value: TagWithMeta[]) => void;
  className?: string;
  placeholder?: string;
}

export function TagList({
  value,
  onChange,
  className,
  placeholder,
}: TagListProps) {
  const { user } = useContext(AppContext);
  const [busy, setBusy] = useState<boolean>(false);
  const handleChange = useCallback(
    (tags: TagWithMeta[]) => {
      // deleted tags
      const deleted = (value || [])
        .filter((tag) => !tags.find((t) => t.id === tag.id))
        .map((e) => ({ ...e, added: false, deleted: true }));
      // added tags
      const added = tags
        .filter((tag) => (value || []).find((t) => t.id === tag.id))
        .map((e) => ({ ...e, added: true, deleted: false }));

      // unchanged tags
      const unchanged = tags.filter(
        (tag) =>
          deleted.find((t) => t.id === tag.id) === undefined &&
          added.find((t) => t.id === tag.id) === undefined
      );

      // merge all
      const merged = [...deleted, ...added, ...unchanged];
      onChange?.(merged);
    },
    [onChange, value]
  );

  const createTag = useCallback(
    async (name: string) => {
      setBusy(true);
      try {
        const { data } = await HttpClient.post(`gear/tags`, {
          name,
        });
        const tags = Array.isArray(value) ? value : [];
        const newList = [...tags, data.data!];
        handleChange(newList);
      } catch (e: any) {
        message.error(e?.message || "Fail to create tag");
      }
      setBusy(false);
    },
    [value, handleChange]
  );
  const loadOptions = useCallback(async (search: string, _, { page }) => {
    try {
      const { data } = await HttpClient.get(
        `gear/tags?name=${search}&page=${page}&size=${size}`
      );
      const options = Array.isArray(data?.data) ? data?.data : [];
      return {
        options,
        hasMore: options.length >= size,
        additional: {
          page: page + 1,
        },
      };
    } catch (e) {}

    return {
      options: [],
      hasMore: false,
    };
  }, []);
  return (
    <Creatable
      isMulti
      value={(value || []).filter((e) => !e.deleted)}
      loadOptions={loadOptions}
      getOptionLabel={(option: any) => {
        return option.name || option.label;
      }}
      onCreateOption={createTag}
      getOptionValue={(option) => option.id}
      isLoading={busy}
      onChange={(values: any) => {
        handleChange(values);
      }}
      additional={{
        page: 1,
      }}
      menuPlacement="auto"
      className={className}
      placeholder={placeholder ?? "Add tags"}
      isValidNewOption={(val) => !!val && canUser(user, Permissions.ADD_GEAR)}
    />
  );
}

export default TagList;
